import React, { useCallback } from "react";
import styled from "styled-components";
import { FontSize, FontWeight, Spacing, content, ellipsis } from "@glossy";
import { getPlatformLabel } from "@linkfluence/i18n";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import Icon from "@ui-kit/icon";
import Tooltip from "@ui-kit/tooltip";
import Link from "@ui-kit/utils/link";
import { tr } from "@radarly/i18n";
import { Post, PostLensFactory } from "@radarly/models/posts";
import Date from "../date";
import Avatar from "./avatar";
import Name from "./name";
import Screenname from "./screenname";

interface IProps {
  post: Post;
  locale: Locale;
  timezone: Timezone;
  width?: number;
  forceInline?: boolean;
  showAvatar?: boolean;
  showPlatform?: boolean;
  showDetails?: boolean;
  isTooltipDisabled?: boolean;
}

const NAME_MAX_WIDTH = 200;
const BREAK_WIDTH = 600;

const Root = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${Spacing.ExtraSmall};
  color: ${content};
  a {
    color: inherit;
  }
`;

const Text = styled.div<{ isNarrow: boolean }>`
  display: flex;
  column-gap: ${Spacing.ExtraSmall};
  flex-direction: ${(props) => (props.isNarrow ? "row" : "column")};
  align-items: baseline;
  font-size: ${FontSize.Small};
  color: ${(props) => content(props, "secondary")};
`;

const StyledName = styled(Name)`
  ${ellipsis(NAME_MAX_WIDTH)};
  display: block;
`;

const NameAndVerified = styled(Link)`
  display: flex;
  gap: ${Spacing.ExtraSmall};
  align-items: center;
`;

const StyledDot = styled.span`
  font-weight: ${FontWeight.Bold};
`;

const RadarlyPostAuthor = ({
  post,
  locale,
  timezone,
  width,
  forceInline = false,
  showAvatar = true,
  showPlatform = true,
  showDetails = false,
  isTooltipDisabled = false,
}: IProps) => {
  const lens = PostLensFactory.create(post);
  const authorUrl = lens.getAuthorPermalink();
  const postContext = lens.hasContext();
  const verified = lens.isAuthorVerified();
  const inline = forceInline || width < BREAK_WIDTH;

  const context =
    postContext &&
    tr(
      locale,
      postContext.label,
      postContext.value && { productName: postContext.value }
    );

  const stopPropagation = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <Root>
      {/* Platform */}
      {showPlatform && (
        <Link url={authorUrl} target="_blank" onClick={stopPropagation}>
          <Tooltip
            disabled={isTooltipDisabled}
            tooltip={getPlatformLabel(locale, post.origin.platform)}
          >
            <div>
              <Icon glyph={post.origin.platform} size={16} />
            </div>
          </Tooltip>
        </Link>
      )}

      {/* Avatar */}
      {showAvatar && (
        <Link url={authorUrl} target="_blank" onClick={stopPropagation}>
          <Avatar size={32} post={post} />
        </Link>
      )}

      {/* Text */}
      <Text isNarrow={inline}>
        {/* Name */}
        <NameAndVerified
          url={authorUrl}
          target="_blank"
          onClick={stopPropagation}
        >
          <StyledName post={post} locale={locale} />
          {!!verified && <Icon color="#3C8DB6" glyph="verified" />}
        </NameAndVerified>

        {/* Screenname */}
        {showDetails && <Screenname post={post} />}

        {/* Date */}
        <Date
          post={post}
          locale={locale}
          timezone={timezone}
          isTooltipDisabled={isTooltipDisabled}
        />

        {/* Context (eg.: "Comment from…") */}
        {showDetails && (
          <>
            {inline && context && <StyledDot>·</StyledDot>}
            {context}
          </>
        )}
      </Text>
    </Root>
  );
};

export default RadarlyPostAuthor;
